export type FloorId = 'ground' | 'upper';
export type Point2 = [number, number];
export type Point3 = [number, number, number];
export interface Bounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}
export interface FloorConfig {
  id: FloorId;
  label: string;
  elevation: number;
  ceiling: number;
  minY: number;
  maxY: number;
  worldBounds: Bounds;
  mapBounds: { x: number; y: number; width: number; height: number };
  mapRotation: number;
}
export interface RoomConfig {
  id: string;
  label: string;
  floor: FloorId;
  bounds: Bounds;
  /** A union of rectangles for rooms that follow the stepped estate facade. */
  footprint?: Bounds[];
  ceiling?: number;
  node: string;
  description: string;
  hero?: boolean;
}
export interface WallConfig {
  id: string;
  floor: FloorId;
  a: Point2;
  b: Point2;
  doors: { center: number; width: number; height?: number; rotation?: number }[];
  ceiling?: number;
}
export interface NavNode {
  id: string;
  position: Point3;
  room: string;
  look?: Point3;
}
export interface FurnitureConfig {
  id: string;
  room: string;
  kind: string;
  x: number;
  z: number;
  width: number;
  depth: number;
  height?: number;
  rotation?: number;
}
export const EYE_HEIGHT = 1.65;
export const ENTRY_FRONT_Z = 7.15;
export const floors: FloorConfig[] = [
  {
    id: 'ground',
    label: 'Ground floor',
    elevation: 0.55,
    ceiling: 3.91,
    minY: -2,
    maxY: 2.4,
    worldBounds: { minX: -22.2, maxX: 11.2, minZ: -6.95, maxZ: 7.15 },
    mapBounds: { x: 16, y: 34.2, width: 288, height: 121.6 },
    mapRotation: 0,
  },
  {
    id: 'upper',
    label: 'Upper floor',
    elevation: 4.25,
    ceiling: 7.5,
    minY: 2.4,
    maxY: 10,
    worldBounds: { minX: -11.2, maxX: 11.2, minZ: -6.95, maxZ: 7.15 },
    mapBounds: { x: 32.1, y: 16, width: 255.8, height: 161 },
    mapRotation: 0,
  },
];
const bounds = (minX: number, maxX: number, minZ: number, maxZ: number): Bounds => ({
  minX,
  maxX,
  minZ,
  maxZ,
});
export const rooms: RoomConfig[] = [
  {
    id: 'foyer',
    label: 'Foyer',
    floor: 'ground',
    bounds: bounds(-3.5, 3.5, 1, 7),
    ceiling: 9.6,
    node: 'foyer',
    description: 'A double-height arrival hall, warm bronze, and a sculptural stair.',
    hero: true,
  },
  {
    id: 'great',
    label: 'Great room',
    floor: 'ground',
    bounds: bounds(-3.5, 3.5, -6.8, 1),
    node: 'great',
    description: 'Dark stone, soft upholstery, and a long view across the pool terrace.',
    hero: true,
  },
  {
    id: 'kitchen',
    label: 'Kitchen',
    floor: 'ground',
    bounds: bounds(3.5, 11, -5.45, 0),
    node: 'kitchen',
    description: 'A veined-stone island anchors smoked walnut cabinetry and bronze detailing.',
    hero: true,
  },
  {
    id: 'dining',
    label: 'Dining',
    floor: 'ground',
    bounds: bounds(3.5, 11, 0, 6.2),
    footprint: [bounds(3.5, 11, 0, 5.1), bounds(7, 11, 5.1, 6.2)],
    node: 'dining',
    description: 'An intimate dining setting beneath a linear bronze pendant.',
  },
  {
    id: 'office',
    label: 'Study',
    floor: 'ground',
    bounds: bounds(-11, -7, -5.45, 1),
    node: 'office',
    description: 'Walnut shelving, a tailored desk, and quiet garden light.',
  },
  {
    id: 'mudroom',
    label: 'Mudroom',
    floor: 'ground',
    bounds: bounds(-11, -7, 1, 6.2),
    footprint: [bounds(-11, -7, 1, 5.1), bounds(-11, -7, 5.1, 6.2)],
    node: 'mudroom',
    description: 'The everyday entrance from the garage, with a walnut bench, coat hooks, and boot storage.',
  },
  {
    id: 'garage-entry',
    label: 'Garage entrance',
    floor: 'ground',
    bounds: bounds(-12.7, -11, 1.5, 4.3),
    node: 'garage-entry',
    description: 'A sheltered connection from the three-car garage into the mudroom.',
  },
  {
    id: 'garage',
    label: 'Three-car garage',
    floor: 'ground',
    bounds: bounds(-22, -12.7, -1.8, 6.1),
    node: 'garage',
    description: 'Three generous parking bays, an epoxy floor, and a dedicated storage and workbench wall.',
  },
  {
    id: 'powder',
    label: 'Powder room',
    floor: 'ground',
    bounds: bounds(-7, -3.5, 3.7, 5.1),
    node: 'powder',
    description: 'A compact stone vanity and softly lit mirror.',
  },
  {
    id: 'ground-hall',
    label: 'Hall',
    floor: 'ground',
    bounds: bounds(-7, -3.5, 1, 3.7),
    node: 'left-hall',
    description: 'The private side of the arrival hall.',
  },
  {
    id: 'stairs-ground',
    label: 'Staircase',
    floor: 'ground',
    bounds: bounds(-7, -3.5, -5.45, 1),
    node: 'stair-foot',
    description: 'A gently paced ascent around a walnut and bronze return stair.',
    hero: true,
  },
  {
    id: 'primary',
    label: 'Primary suite',
    floor: 'upper',
    bounds: bounds(-3.5, 3.5, -6.8, 0.5),
    ceiling: 9.6,
    node: 'primary',
    description: 'A layered headboard, warm cove lighting, and a private terrace outlook.',
    hero: true,
  },
  {
    id: 'bath',
    label: 'Primary bath',
    floor: 'upper',
    bounds: bounds(3.5, 11, -5.45, -2.3),
    node: 'bath',
    description: 'Veined dark marble, a double vanity, and a freestanding bath.',
    hero: true,
  },
  {
    id: 'closet',
    label: 'Dressing room',
    floor: 'upper',
    bounds: bounds(3.5, 11, -2.3, 0.5),
    node: 'closet',
    description: 'Warm wood storage and illuminated display niches.',
  },
  {
    id: 'bedroom-2',
    label: 'Bedroom 02',
    floor: 'upper',
    bounds: bounds(-11, -3.5, 2.1, 6.2),
    footprint: [bounds(-11, -3.5, 2.1, 5.1), bounds(-11, -7, 5.1, 6.2)],
    node: 'bedroom-2',
    description: 'A quiet guest room overlooking the arrival garden.',
  },
  {
    id: 'bedroom-3',
    label: 'Bedroom 03',
    floor: 'upper',
    bounds: bounds(3.5, 11, 2.1, 6.2),
    footprint: [bounds(3.5, 11, 2.1, 5.1), bounds(7, 11, 5.1, 6.2)],
    node: 'bedroom-3',
    description: 'Textured fabrics and a restrained warm palette.',
  },
  {
    id: 'bedroom-4',
    label: 'Bedroom 04',
    floor: 'upper',
    bounds: bounds(-11, -7, -5.45, 0.5),
    node: 'bedroom-4',
    description: 'A secluded room on the garden side of the house.',
  },
  {
    id: 'stairs-upper',
    label: 'Staircase',
    floor: 'upper',
    bounds: bounds(-7, -3.5, -5.45, 0.5),
    node: 'stair-upper',
    description: 'The stair arrives beside the upper gallery.',
  },
  {
    id: 'upper-hall',
    label: 'Upper gallery',
    floor: 'upper',
    bounds: bounds(-11, 11, 0.5, 2.1),
    node: 'upper-hall',
    description: 'A light-filled gallery linking the bedroom wings.',
  },
];
const wall = (
  id: string,
  floor: FloorId,
  a: Point2,
  b: Point2,
  doors: WallConfig['doors'] = [],
): WallConfig => ({ id, floor, a, b, doors });
export const walls: WallConfig[] = [
  wall('g-left', 'ground', [-3.5, -6.8], [-3.5, 7], [{ center: 2.1, width: 1.5 }]),
  wall(
    'g-right',
    'ground',
    [3.5, -6.8],
    [3.5, 7],
    [
      { center: -1.2, width: 1.65 },
      { center: 3, width: 1.65 },
    ],
  ),
  wall('g-great', 'ground', [-3.5, 1], [3.5, 1], [{ center: 0, width: 4.6, height: 3.2 }]),
  wall('g-kitchen', 'ground', [3.5, 0], [11, 0], [{ center: 5.15, width: 1.65 }]),
  wall(
    'g-study',
    'ground',
    [-7, -5.45],
    [-7, 5.1],
    [
      { center: 0.7, width: 1.1 },
      { center: 2.65, width: 1.3 },
    ],
  ),
  wall('g-study-front', 'ground', [-11, 1], [-7, 1]),
  wall('g-powder', 'ground', [-7, 3.7], [-3.5, 3.7], [{ center: -5, width: 1.05 }]),
  wall('g-garage-house', 'ground', [-11, 1.5], [-11, 4.3], [{ center: 2.8, width: 1.35 }]),
  wall('g-garage-link', 'ground', [-12.7, 1.5], [-12.7, 4.3], [{ center: 2.8, width: 1.35 }]),
  { ...wall('u-primary-left', 'upper', [-3.5, -6.8], [-3.5, 0.5]), ceiling: 9.6 },
  { ...wall('u-primary-right', 'upper', [3.5, -6.8], [3.5, 0.5]), ceiling: 9.6 },
  {
    ...wall('u-primary-hall', 'upper', [-3.5, 0.5], [3.5, 0.5], [{ center: 0, width: 1.6 }]),
    ceiling: 9.6,
  },
  wall('u-dressing-hall', 'upper', [3.5, 0.5], [11, 0.5], [{ center: 6.1, width: 1.2 }]),
  wall('u-bath', 'upper', [3.5, -2.3], [11, -2.3], [{ center: 6.1, width: 1.2 }]),
  wall('u-bed2', 'upper', [-11, 2.1], [-3.5, 2.1], [{ center: -7.5, width: 1.2 }]),
  wall('u-bed3', 'upper', [3.5, 2.1], [11, 2.1], [{ center: 7.5, width: 1.2 }]),
  wall('u-bed4', 'upper', [-11, 0.5], [-7, 0.5], [{ center: -9, width: 1.2 }]),
  wall('u-stair', 'upper', [-7, -5.45], [-7, 0.5]),
  { ...wall('u-foyer-left', 'upper', [-3.5, 2.1], [-3.5, 7]), ceiling: 9.6 },
  { ...wall('u-foyer-right', 'upper', [3.5, 2.1], [3.5, 7]), ceiling: 9.6 },
];
export function wallSegments(w: WallConfig): { a: Point2; b: Point2; top: boolean; doorHeight: number }[] {
  const horizontal = w.a[1] === w.b[1],
    start = horizontal ? w.a[0] : w.a[1],
    end = horizontal ? w.b[0] : w.b[1];
  const point = (value: number): Point2 => (horizontal ? [value, w.a[1]] : [w.a[0], value]);
  const segments: { a: Point2; b: Point2; top: boolean; doorHeight: number }[] = [];
  let cursor = start;
  for (const door of [...w.doors].sort((a, b) => a.center - b.center)) {
    const lo = door.center - door.width / 2,
      hi = door.center + door.width / 2;
    if (lo > cursor) segments.push({ a: point(cursor), b: point(lo), top: false, doorHeight: 0 });
    segments.push({ a: point(lo), b: point(hi), top: true, doorHeight: door.height ?? 2.5 });
    cursor = hi;
  }
  if (cursor < end) segments.push({ a: point(cursor), b: point(end), top: false, doorHeight: 0 });
  return segments;
}
const node = (id: string, x: number, y: number, z: number, room: string, look?: Point3): NavNode => ({
  id,
  position: [x, y + EYE_HEIGHT, z],
  room,
  look,
});
export const navigationNodes: NavNode[] = [
  node('foyer', 0, 0.55, 4.5, 'foyer', [0, 2.8, -2]),
  node('great', 0, 0.55, -0.65, 'great', [-1.5, 1.8, -4]),
  node('great-east', 0, 0.55, -1.2, 'great'),
  node('kitchen', 5.15, 0.55, -1.2, 'kitchen', [8, 1.55, -3.6]),
  node('foyer-east', 0, 0.55, 3, 'foyer'),
  node('dining', 5.15, 0.55, 2.4, 'dining', [8, 1.6, 3.65]),
  node('left-hall', -5, 0.55, 2.1, 'ground-hall', [-6.25, 3, -2.5]),
  node('foyer-west', 0, 0.55, 2.1, 'foyer'),
  node('study-door', -5, 0.55, 0.7, 'stairs-ground'),
  node('office-entry', -8.2, 0.55, 0.7, 'office'),
  node('office', -9.7, 0.55, -0.3, 'office', [-9.15, 1.7, -3.05]),
  node('mudroom', -8.2, 0.55, 2.8, 'mudroom', [-9.3, 1.8, 5.2]),
  node('garage-entry', -11.85, 0.55, 2.8, 'garage-entry', [-15.4, 2.05, 2.8]),
  node('garage-threshold', -13.35, 0.55, 2.8, 'garage'),
  node('garage', -17.5, 0.55, 0.5, 'garage', [-18.1, 2, 6.1]),
  node('mudroom-door', -5, 0.55, 2.65, 'ground-hall'),
  node('powder', -5, 0.55, 4.4, 'powder', [-6.45, 1.7, 4.6]),
  node('stair-foot', -6.15, 0.55, 0.7, 'stairs-ground', [-6.15, 3.2, -4]),
  node('stair-lower', -6.15, 0.55, 0.15, 'stairs-ground'),
  node('stair-turn-a', -6.15, 2.4, -4.75, 'stairs-ground'),
  node('stair-turn-b', -4.55, 2.4, -4.75, 'stairs-upper'),
  node('stair-upper', -4.55, 4.25, 0.15, 'stairs-upper'),
  node('stair-top', -4.55, 4.25, 1.3, 'upper-hall', [0, 5.9, 1.3]),
  node('upper-hall', 0, 4.25, 1.3, 'upper-hall', [0, 6, 5.3]),
  node('primary', 0, 4.25, -0.6, 'primary', [-1.8, 5.4, -3]),
  node('upper-east', 6.1, 4.25, 1.3, 'upper-hall'),
  node('closet-door', 6.1, 4.25, -0.7, 'closet'),
  node('closet', 6.8, 4.25, -0.2, 'closet', [8.9, 5.7, -1.85]),
  node('bath', 6.1, 4.25, -3.05, 'bath', [9.2, 5.5, -4.2]),
  node('upper-west', -7.5, 4.25, 1.3, 'upper-hall'),
  node('bedroom-2', -7.5, 4.25, 2.65, 'bedroom-2', [-5.4, 5.4, 3.6]),
  node('upper-east-front', 7.5, 4.25, 1.3, 'upper-hall'),
  node('bedroom-3', 7.5, 4.25, 2.65, 'bedroom-3', [5.4, 5.4, 3.6]),
  node('upper-far-west', -9, 4.25, 1.3, 'upper-hall'),
  node('bedroom-4', -9, 4.25, -0.25, 'bedroom-4', [-8.3, 5.4, -3.3]),
];
export const navigationEdges: [string, string][] = [
  ['foyer', 'foyer-east'],
  ['foyer-east', 'foyer-west'],
  ['foyer-west', 'great'],
  ['great', 'great-east'],
  ['great-east', 'kitchen'],
  ['foyer-east', 'dining'],
  ['dining', 'kitchen'],
  ['foyer-west', 'left-hall'],
  ['left-hall', 'study-door'],
  ['stair-foot', 'office-entry'],
  ['office-entry', 'office'],
  ['left-hall', 'mudroom-door'],
  ['mudroom-door', 'mudroom'],
  ['mudroom', 'garage-entry'],
  ['garage-entry', 'garage-threshold'],
  ['garage-threshold', 'garage'],
  ['mudroom-door', 'powder'],
  ['study-door', 'stair-foot'],
  ['stair-foot', 'stair-lower'],
  ['stair-lower', 'stair-turn-a'],
  ['stair-turn-a', 'stair-turn-b'],
  ['stair-turn-b', 'stair-upper'],
  ['stair-upper', 'stair-top'],
  ['stair-top', 'upper-hall'],
  ['upper-hall', 'primary'],
  ['upper-hall', 'upper-east'],
  ['upper-east', 'closet-door'],
  ['closet-door', 'closet'],
  ['closet-door', 'bath'],
  ['stair-top', 'upper-west'],
  ['upper-west', 'bedroom-2'],
  ['upper-east', 'upper-east-front'],
  ['upper-east-front', 'bedroom-3'],
  ['upper-west', 'upper-far-west'],
  ['upper-far-west', 'bedroom-4'],
];
export const furniture: FurnitureConfig[] = [
  { id: 'foyer-console', room: 'foyer', kind: 'console', x: 2.9, z: 4.7, width: 0.65, depth: 2.1 },
  { id: 'great-sofa', room: 'great', kind: 'sofa', x: 0.5, z: -4, width: 3.3, depth: 1.2 },
  { id: 'great-table', room: 'great', kind: 'coffee', x: 0.5, z: -2.5, width: 2.2, depth: 0.8 },
  { id: 'great-fireplace', room: 'great', kind: 'fireplace', x: -3.12, z: -3.5, width: 0.45, depth: 2.8 },
  { id: 'kitchen-island', room: 'kitchen', kind: 'island', x: 7.7, z: -2.9, width: 3.35, depth: 1.5 },
  { id: 'kitchen-cabinets', room: 'kitchen', kind: 'kitchen', x: 7.4, z: -4.95, width: 6.6, depth: 0.7 },
  { id: 'dining-table', room: 'dining', kind: 'dining', x: 8, z: 3.35, width: 3.1, depth: 1.25 },
  { id: 'office-desk', room: 'office', kind: 'desk', x: -9.15, z: -3.05, width: 2.3, depth: 0.95 },
  { id: 'office-shelf', room: 'office', kind: 'shelf', x: -10.55, z: -2.2, width: 0.5, depth: 5.2 },
  {
    id: 'mudroom-bench',
    room: 'mudroom',
    kind: 'mudroom-bench',
    x: -9.3,
    z: 5.5,
    width: 2.7,
    depth: 0.62,
    rotation: Math.PI,
  },
  { id: 'garage-workbench', room: 'garage', kind: 'workbench', x: -17.45, z: -1.27, width: 4.1, depth: 0.68 },
  {
    id: 'garage-storage',
    room: 'garage',
    kind: 'garage-storage',
    x: -20.85,
    z: -1.23,
    width: 1.7,
    depth: 0.76,
  },
  { id: 'powder-vanity', room: 'powder', kind: 'vanity', x: -6.55, z: 4.55, width: 0.55, depth: 1.15 },
  {
    id: 'primary-bed',
    room: 'primary',
    kind: 'bed',
    rotation: Math.PI / 2,
    x: -1.7,
    z: -3,
    width: 2.8,
    depth: 2.5,
  },
  { id: 'bath-tub', room: 'bath', kind: 'tub', x: 9.15, z: -4.3, width: 2.1, depth: 1.1 },
  { id: 'bath-vanity', room: 'bath', kind: 'vanity', x: 4.1, z: -4, width: 0.65, depth: 2.5 },
  { id: 'bath-shower', room: 'bath', kind: 'shower', x: 10.25, z: -2.95, width: 1.2, depth: 1.1 },
  { id: 'closet-cabinet', room: 'closet', kind: 'wardrobe', x: 8.9, z: -1.97, width: 3.55, depth: 0.55 },
  { id: 'bed2', room: 'bedroom-2', kind: 'bed', x: -5.4, z: 3.5, width: 2.1, depth: 2.1 },
  { id: 'bed3', room: 'bedroom-3', kind: 'bed', x: 5.4, z: 3.5, width: 2.1, depth: 2.1 },
  {
    id: 'bed4',
    room: 'bedroom-4',
    kind: 'bed',
    rotation: -Math.PI / 2,
    x: -8.45,
    z: -3.3,
    width: 2.1,
    depth: 2.1,
  },
];
export const roomById = (id: string) => rooms.find((r) => r.id === id)!;
export const floorById = (id: FloorId) => floors.find((f) => f.id === id)!;
export const nodeById = (id: string) => navigationNodes.find((n) => n.id === id)!;

export const roomFootprints = (room: RoomConfig): Bounds[] => room.footprint ?? [room.bounds];
export const containsPoint = (b: Bounds, x: number, z: number): boolean =>
  x >= b.minX && x <= b.maxX && z >= b.minZ && z <= b.maxZ;
export const roomContainsPoint = (room: RoomConfig, x: number, z: number): boolean =>
  roomFootprints(room).some((b) => containsPoint(b, x, z));
/** Occupied floor outlines; the upper foyer void is included only for drawing the shell. */
export function floorFootprints(floor: FloorId, includeVoids = false): Bounds[] {
  const footprint = rooms.filter((room) => room.floor === floor).flatMap(roomFootprints);
  if (floor === 'upper' && includeVoids) footprint.push(bounds(-3.5, 3.5, 2.1, 7));
  return footprint;
}

/** Walking surfaces and structural openings are shared with the tour and shell. */
export const stair = {
  lower: floors[0].elevation,
  upper: floors[1].elevation,
  landing: 2.4,
  lowerX: -6.15,
  upperX: -4.55,
  width: 1.28,
  steps: 11,
  front: 0.15,
  rear: -4.4,
  turn: -4.75,
  thickness: 0.085,
  opening: { minX: -6.95, maxX: -3.75, minZ: -5.35, maxZ: 0.55 },
  upperLanding: { minX: -5.2, maxX: -3.9, minZ: 0.13, maxZ: 0.65 },
};
export function stairSurface(x: number, z: number): number | undefined {
  if (z >= -5.22 && z <= stair.rear && x >= -6.8 && x <= -3.9) return stair.landing;
  if (z >= stair.front && z <= 0.65 && Math.abs(x - stair.upperX) <= stair.width / 2) return stair.upper;
  if (z < stair.rear || z > stair.front) return undefined;
  const lower = Math.abs(x - stair.lowerX) <= stair.width / 2,
    upper = Math.abs(x - stair.upperX) <= stair.width / 2;
  if (!lower && !upper) return undefined;
  const t = lower
    ? (stair.front - z) / (stair.front - stair.rear)
    : (z - stair.rear) / (stair.front - stair.rear);
  return (
    (lower ? stair.lower : stair.landing) +
    (Math.max(0, Math.min(stair.steps, Math.ceil(t * stair.steps))) * (stair.landing - stair.lower)) /
      stair.steps
  );
}

/** A continuous walking envelope anticipates each riser instead of jolting the eye. */
export function stairWalkingHeight(x: number, z: number): number | undefined {
  const rise = (stair.landing - stair.lower) / stair.steps;
  const smooth = (t: number) => {
    t = Math.max(0, Math.min(1, t));
    return t * t * (3 - 2 * t);
  };
  if (Math.abs(x - stair.lowerX) < stair.width / 2 && z <= 0.7 && z >= stair.rear) {
    if (z > stair.front) return stair.lower + rise * smooth((0.7 - z) / (0.7 - stair.front));
    return Math.min(
      stair.landing,
      stair.lower + ((stair.front - z) / (stair.front - stair.rear)) * (stair.landing - stair.lower) + rise,
    );
  }
  if (Math.abs(x - stair.upperX) < stair.width / 2 && z >= stair.turn && z <= 0.65) {
    if (z < stair.rear) return stair.landing + rise * smooth((z - stair.turn) / (stair.rear - stair.turn));
    return Math.min(
      stair.upper,
      stair.landing + ((z - stair.rear) / (stair.front - stair.rear)) * (stair.upper - stair.landing) + rise,
    );
  }
  return stairSurface(x, z);
}

export function furnitureBounds(item: FurnitureConfig): Bounds {
  const c = Math.abs(Math.cos(item.rotation ?? 0)),
    s = Math.abs(Math.sin(item.rotation ?? 0));
  const w = item.width * c + item.depth * s,
    d = item.width * s + item.depth * c;
  return { minX: item.x - w / 2, maxX: item.x + w / 2, minZ: item.z - d / 2, maxZ: item.z + d / 2 };
}

// Guided visits follow the ground-floor circuit, then the upper floor west to east.
export const guidedTourStops = [
  'foyer',
  'great',
  'kitchen',
  'dining',
  'left-hall',
  'powder',
  'mudroom',
  'garage-entry',
  'garage',
  'office',
  'stair-foot',
  'stair-upper',
  'bedroom-4',
  'bedroom-2',
  'upper-hall',
  'primary',
  'closet',
  'bath',
  'bedroom-3',
];
